import React from 'react'
import { guestName, stayMomentLabel, WINDOW_DAYS } from './fieldStays'
import { formatShortDate } from './fieldDates'
import { pitchSubtitle } from './fieldSearch'
import { CheckIcon, NoResultIcon, SuitcaseIcon, ChevronRight } from './FieldIcons'
import { FLOW_TYPES, Pastille, BackButton, SectionTitle, ListGroup, ListRow, HelpText, Notice, LoadingText, LinkButton, PrimaryButton, SecondaryButton, BottomBar } from './FieldUI'
import { colors } from './fieldTheme'

// Séjour proposé (canvas W6). Décisions 2 et 16.
// `manual` : séjour choisi à la main (numéro de séjour), on ne parle plus de « proposé ».

function capitalize(text)
{
	return text.charAt(0).toUpperCase() + text.slice(1)
}

function PitchHero({ type, pitch })
{
	return (
		<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '24px 20px 0', textAlign: 'center' }}>
			<Pastille type={type} size={120} illustrationSize={82} />
			<span style={{ fontSize: 17, fontWeight: 700, color: colors.textSecondary, marginTop: 4 }}>{FLOW_TYPES[type].title}</span>
			<h1 style={{ fontSize: 60, fontWeight: 800, lineHeight: 1, color: colors.text, letterSpacing: '-.01em', overflowWrap: 'anywhere' }}>{pitch.location}</h1>
			<span style={{ fontSize: 17, fontWeight: 600, color: colors.textSecondary, whiteSpace: 'pre-line' }}>{pitchSubtitle(pitch)}</span>
		</div>
	)
}

function GuestInventoryRow({ inventory, now })
{
	const moment = inventory.type === 'departure' ? 'au départ' : 'à l\'arrivée'
	return (
		<div style={{ minHeight: 64, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 20px', borderTop: '1px solid ' + colors.separator }}>
			<span style={{ width: 40, height: 40, borderRadius: 999, background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
				<CheckIcon size={22} color="#14532D" />
			</span>
			<span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
				<span style={{ fontSize: 17, fontWeight: 700 }}>Inventaire du vacancier fait</span>
				<span style={{ fontSize: 15, fontWeight: 500, color: colors.textSecondary }}>
					{'Le ' + formatShortDate(inventory.create_time, now) + ', ' + moment + ' : vos comptages y seront comparés'}
				</span>
			</span>
		</div>
	)
}

export default function FieldStayProposal({ type, pitch, status, stay, client, guestInventory, manual, hasOtherStays, now, onConfirm, onOtherStays, onStayNumber, onRetry, onBack })
{
	const isDeparture = type === 'departure'
	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
			<BackButton label={manual ? 'Numéro de séjour' : 'Emplacement'} onClick={onBack} />
			<PitchHero type={type} pitch={pitch} />

			{status === 'loading' && <LoadingText>Recherche du séjour…</LoadingText>}
			{status === 'error' && (
				<Notice
					icon={<NoResultIcon />}
					title="Impossible de charger les séjours"
					hint="Vérifiez la connexion, puis réessayez."
					action={<div style={{ marginLeft: -12 }}><LinkButton onClick={onRetry}>Réessayer</LinkButton></div>}
				/>
			)}

			{status === 'ready' && stay && (
				<React.Fragment>
					<SectionTitle>{manual ? 'Séjour choisi' : 'Séjour proposé'}</SectionTitle>
					<div style={{ margin: '0 20px', background: colors.surface, borderRadius: 22, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 20 }}>
							<span style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, overflowWrap: 'break-word' }}>{guestName(client) || 'Séjour n° ' + stay.stay_number}</span>
							<span style={{ fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>{'Séjour n° ' + stay.stay_number}</span>
							<span style={{ fontSize: 17, fontWeight: 600, color: colors.textSecondary }}>{capitalize(stayMomentLabel(stay, type, now))}</span>
						</div>
						{guestInventory && <GuestInventoryRow inventory={guestInventory} now={now} />}
					</div>
					{!manual && (
						<HelpText>
							{isDeparture
								? 'Proposé : le séjour le plus proche dont la sortie n\'est pas faite, sur les ' + WINDOW_DAYS + ' derniers jours.'
								: 'Proposé : le séjour le plus proche dont l\'entrée n\'est pas faite, sur les ' + WINDOW_DAYS + ' prochains jours.'}
						</HelpText>
					)}
					<BottomBar>
						<SecondaryButton onClick={onOtherStays}>Pas le bon séjour ?</SecondaryButton>
						<PrimaryButton icon={<CheckIcon />} onClick={onConfirm}>Confirmer</PrimaryButton>
					</BottomBar>
				</React.Fragment>
			)}

			{status === 'ready' && !stay && (
				<React.Fragment>
					<Notice
						icon={<NoResultIcon />}
						title={'Aucun séjour à proposer sur ' + pitch.location}
						hint={isDeparture
							? 'Aucune sortie à faire sur les ' + WINDOW_DAYS + ' derniers jours.'
							: 'Aucune entrée à faire sur les ' + WINDOW_DAYS + ' prochains jours.'}
					/>
					<SectionTitle>Trouver le séjour</SectionTitle>
					<ListGroup indent={20}>
						{hasOtherStays && (
							<ListRow title={'Voir les séjours de ' + pitch.location} titleSize={19} minHeight={64} onClick={onOtherStays} trailing={<ChevronRight />} />
						)}
						<ListRow title="Saisir un numéro de séjour" titleSize={19} minHeight={64} onClick={onStayNumber} trailing={<span style={{ color: colors.primary, display: 'flex' }}><SuitcaseIcon /></span>} />
					</ListGroup>
				</React.Fragment>
			)}
		</div>
	)
}
